import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, ArrowRight, Globe2, Shield, Layers, Users, Zap,
  Building2, Mail, Phone, MapPin, ChevronLeft,
} from 'lucide-react';
import MainNav from '@/components/layout/MainNav';
import Footer from '@/components/layout/Footer';
import AdinkraPattern from '@/components/layout/AdinkraPattern';
import AIChatWidget from '@/components/AIChatWidget';
import { MODULES } from '@/lib/modules';
import api from '@/lib/api';
import { formatApiError } from '@/lib/auth';

// Rotating gallery images — factories, offices, technology centres, landscapes
const GALLERY = [
  { url: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=1200&q=80', label: 'Manufacturing & Industry' },
  { url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80', label: 'Corporate Offices' },
  { url: 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=1200&q=80', label: 'Technology Centres' },
  { url: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&q=80', label: 'Business Landscapes' },
  { url: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1200&q=80', label: 'Workspace & Operations' },
  { url: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1200&q=80', label: 'Enterprise Workflow' },
];

// Module value text — what it does, who it is for (only 9 modules now)
const MODULE_VALUES = {
  'financials': { does: 'A complete finance and asset control engine. Manage your accounts, invoices, budgets, assets, procurement, and maintenance — all connected with no duplicate entry.', for: 'Companies · Schools · Estates · NGOs · Institutions' },
  'estate': { does: 'Full estate operations management. Housing, power, security, water, vehicles — every facility under one dashboard.', for: 'Residential estates · Gated communities · Institutional campuses' },
  'offices': { does: 'Operational control for office environments. ICT assets, facility bookings, procurement, security, staff operations.', for: 'Companies · Firms · Government offices · Multi-branch organisations' },
  'education': { does: 'Built for schools. Finance, fee management, hostel, library, lab, clinic, kitchen, ICT — purpose-built for education.', for: 'Schools · Universities · Training institutions · Boarding schools' },
  'events': { does: 'End-to-end event management, QR-based access control, and automated certificate issuance — fully traceable.', for: 'Schools · Corporates · Governments · Training providers · Certification bodies' },
  'lms': { does: 'Structured learning with locked progression. Foundation → Intermediate → Professional → Specialist with badges and certifications.', for: 'Individuals · Training organisations · Schools · Professional development' },
  'healthcare': { does: 'EMR, OPD queue, vitals, lab, pharmacy stock, ward, billing & HMO — designed for hospitals, clinics and pharmacies.', for: 'Hospitals · Clinics · Diagnostic centres · Pharmacies · Dispensaries' },
  'hr': { does: 'Enterprise HCM with Master Data Architecture. Workforce planning, recruitment, payroll, performance, learning, succession, compliance — built for 50 to 100,000+ employees.', for: 'Schools · Hospitals · Manufacturing · Banks · Government · NGOs · Conglomerates · Multi-country organisations' },
  'engineering': { does: 'Olu-Akintade Computer & Electrical Works Engineering. Electrical installation, computer repairs, electronics, CCTV, network.', for: 'Households · Estates · Schools · Corporate clients' },
  'corporate': { does: 'Professional consulting, training and education practice for African institutions. Run by Olusegun Akintade.', for: 'Schools · Corporates · Government · NGOs · Boarding institutions' },
};

// Currencies with flags & rates (rates indicative — adjust in Plans section)
const CURRENCY_RATES = {
  NGN: { symbol: '₦', flag: '🇳🇬', name: 'Naira', rate: 1 },
  GBP: { symbol: '£', flag: '🇬🇧', name: 'Pound', rate: 1 / 2050 },
  USD: { symbol: '$', flag: '🇺🇸', name: 'Dollar', rate: 1 / 1600 },
};

const fmtPrice = (ngn) => {
  if (typeof ngn !== 'number') return ngn;
  const gbp = (ngn * CURRENCY_RATES.GBP.rate).toFixed(0);
  const usd = (ngn * CURRENCY_RATES.USD.rate).toFixed(0);
  return { ngn: `₦${ngn.toLocaleString()}`, gbp: `£${Number(gbp).toLocaleString()}`, usd: `$${Number(usd).toLocaleString()}` };
};

// HQs in 4 countries
const HEADQUARTERS = [
  { city: 'Lagos', state: 'Lagos State', country: 'Nigeria', flag: '🇳🇬' },
  { city: 'London', state: 'England', country: 'United Kingdom', flag: '🇬🇧' },
  { city: 'Houston', state: 'Texas', country: 'United States', flag: '🇺🇸' },
  { city: 'Toronto', state: 'Ontario', country: 'Canada', flag: '🇨🇦' },
];

export default function MainLanding() {
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [hoveredModule, setHoveredModule] = useState(null);

  // Rotate gallery every 5s
  useEffect(() => {
    const t = setInterval(() => setGalleryIndex((i) => (i + 1) % GALLERY.length), 5000);
    return () => clearInterval(t);
  }, []);

  // Scroll to hash on load
  useEffect(() => {
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  return (
    <div className="bg-white text-navy" data-testid="main-landing">
      <MainNav />

      {/* HERO with rotating gallery */}
      <section className="relative overflow-hidden bg-navy text-white">
        <div className="absolute inset-0 opacity-[0.06]"><AdinkraPattern color="#D4A437" /></div>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-amber/20 blur-[120px]" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-cyan/10 blur-[120px]" />

        <div className="container-x relative pt-20 pb-28 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-white/5 backdrop-blur-sm rounded-full text-xs uppercase tracking-[0.18em] mb-7">
                <span className="h-1.5 w-1.5 rounded-full bg-amber animate-pulse" />
                Technology · Business · Growth
              </div>
              <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] text-balance">
                Powering Businesses Through{' '}
                <span className="relative inline-block">
                  <span className="text-amber">Smart Technology</span>
                  <span className="absolute -bottom-1 left-0 right-0 h-[6px] bg-gold/60 -z-10" />
                </span>{' '}
                Systems.
              </h1>
              <p className="mt-6 text-lg text-white/75 leading-relaxed max-w-2xl">
                ATTP NEXUS is built for organisations that want structure, visibility and control.
                From operations to finance, from people to processes — everything works together in
                one connected system designed for growth.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="#modules" className="btn-accent text-base" data-testid="hero-cta-explore">
                  Explore Modules <ChevronRight className="w-4 h-4" />
                </a>
                <a href="#contact" className="btn-ghost-dark text-base" data-testid="hero-cta-contact">
                  Contact Us
                </a>
              </div>
            </div>

            {/* Rotating gallery */}
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[4/5] rounded-md overflow-hidden border border-white/10 shadow-2xl bg-navy-900">
                {GALLERY.map((img, i) => (
                  <img
                    key={i}
                    src={img.url}
                    alt={img.label}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                    style={{ opacity: i === galleryIndex ? 1 : 0 }}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 backdrop-blur-md bg-white/10 border border-white/20 rounded-md p-4">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-white/60 mb-1">In view</div>
                  <div className="text-sm text-white/95 leading-snug font-medium">{GALLERY[galleryIndex].label}</div>
                  <div className="mt-3 flex items-center gap-1.5">
                    {GALLERY.map((_, i) => (
                      <button key={i} onClick={() => setGalleryIndex(i)}
                        className={`h-1 rounded-full transition-all ${i === galleryIndex ? 'w-8 bg-amber' : 'w-2 bg-white/30 hover:bg-white/50'}`}
                        aria-label={`Image ${i + 1}`}
                        data-testid={`hero-dot-${i}`}
                      />
                    ))}
                  </div>
                </div>
                <button onClick={() => setGalleryIndex((galleryIndex - 1 + GALLERY.length) % GALLERY.length)} className="absolute left-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setGalleryIndex((galleryIndex + 1) % GALLERY.length)} className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="absolute -bottom-6 -left-6 bg-white text-navy rounded-md shadow-xl border border-neutral-200 p-4 flex items-center gap-3 max-w-[280px] hidden md:flex">
                <img src="/logos/oluakin-engineering.png" alt="Olu-Akintade Engineering" className="h-12 object-contain" />
                <div className="text-[11px] leading-snug">
                  <div className="font-semibold text-navy">In affiliation with</div>
                  <Link to="/engineering" className="text-neutral-600 hover:text-amber transition-colors">Olu-Akintade Computer & Electrical Works Engineering</Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10 bg-black/20 py-4">
          <div className="container-x flex flex-wrap items-center justify-between gap-y-3 gap-x-8 text-xs uppercase tracking-[0.2em] text-white/50">
            <span>Multi-tenant by design</span><span>·</span>
            <span>NDPR · GDPR aligned</span><span>·</span>
            <span>AES-256 encryption</span><span>·</span>
            <span>Multi-currency · ₦ £ $</span><span>·</span>
            <span>Built in Africa, serving the world</span>
          </div>
        </div>
      </section>

      {/* ABOUT (anchor) */}
      <section id="about" className="py-20 bg-white">
        <div className="container-x">
          <div className="max-w-3xl">
            <div className="label-tag text-amber mb-4">About ATTP NEXUS</div>
            <h2 className="font-display font-bold text-4xl tracking-tight leading-tight text-balance">
              A unified digital ecosystem for business operations and growth.
            </h2>
            <p className="mt-5 text-lg text-neutral-600 leading-relaxed">
              ATTP NEXUS is a multi-module digital platform that integrates finance, asset management,
              operations, procurement, customer engagement and reporting into one connected workspace.
              Designed for African organizations and built for global standards, it provides the
              structure, transparency and intelligence enterprises need to operate efficiently and grow
              sustainably.
            </p>
          </div>
        </div>
      </section>

      {/* VISION · MISSION · CORE VALUES — moved UP, before modules */}
      <section id="vision" className="py-20 lg:py-24 bg-neutral-50">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <div className="label-tag text-amber mb-4">What we stand for</div>
            <h2 className="font-display font-bold text-4xl tracking-tight leading-[1.05] text-balance">
              Built on a clear vision. Driven by uncompromising values.
            </h2>
          </div>

          <div id="mission" className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
            <div className="lg:col-span-6 p-8 bg-navy text-white rounded-md relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-amber/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="label-tag text-amber mb-4">Vision</div>
                <p className="font-display font-bold text-2xl lg:text-3xl leading-tight text-balance">
                  To build a unified digital ecosystem where businesses operate with clarity,
                  efficiency and measurable growth.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6 p-8 bg-amber/10 border border-amber/30 rounded-md">
              <div className="label-tag text-amber-700 mb-4">Mission</div>
              <p className="font-display font-semibold text-xl lg:text-2xl leading-tight text-navy text-balance">
                To deliver structured, scalable and intelligent tools that simplify operations,
                strengthen decision-making and support business expansion across every sector.
              </p>
            </div>
          </div>

          <div id="values">
            <div className="label-tag text-amber mb-4">Core Values</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { t: 'Structure', d: 'Every workflow is predictable, repeatable and audit-ready by design.' },
                { t: 'Clarity', d: 'Transparent data, transparent pricing, transparent decisions.' },
                { t: 'Efficiency', d: 'One entry — system-wide update. No double work, ever.' },
                { t: 'Accountability', d: 'Audit trails, signed documents, immutable logs across every transaction.' },
                { t: 'Growth', d: 'Tools that scale from a five-person team to a thousand-strong enterprise.' },
                { t: 'Integrity', d: 'African-built, world-grade engineering — held to the highest standards.' },
              ].map((v) => (
                <div key={v.t} className="p-6 bg-white border border-neutral-200 rounded-md hover:border-amber/40 hover:shadow-sm transition-all">
                  <div className="font-display font-bold text-lg text-navy mb-1">{v.t}</div>
                  <p className="text-sm text-neutral-600 leading-relaxed">{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MODULES — interactive hover-expand cards */}
      <section id="modules" className="py-20 lg:py-24 bg-white">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <div className="label-tag text-amber mb-4">The Modules</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05] text-balance">
              One Platform. Every Function Your Business Needs.
            </h2>
            <p className="mt-5 text-lg text-neutral-600 leading-relaxed">
              Select the module that fits your organisation. Each one is a fully built operational
              environment — not just a tool, but a complete system.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {MODULES.map((m) => {
              const v = MODULE_VALUES[m.slug] || { does: m.description, for: 'Organizations of all sizes' };
              const isHover = hoveredModule === m.slug;
              const Icon = m.icon;
              // Public-site modules route to their dedicated full sites
              const href = m.slug === 'engineering' ? '/engineering'
                         : m.slug === 'corporate' ? '/corporate'
                         : `/m/${m.slug}`;
              return (
                <Link
                  key={m.id}
                  to={href}
                  onMouseEnter={() => setHoveredModule(m.slug)}
                  onMouseLeave={() => setHoveredModule(null)}
                  className="group relative bg-white border border-neutral-200 rounded-md p-5 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer"
                  style={{
                    background: isHover ? `linear-gradient(135deg, ${m.accent}10, ${m.accent}03)` : 'white',
                    borderColor: isHover ? m.accent : '#E5E7EB',
                    transform: isHover ? 'translateY(-6px) scale(1.02)' : 'translateY(0)',
                  }}
                  data-testid={`module-card-${m.slug}`}
                >
                  <span className="absolute top-0 left-0 right-0 h-0.5 transition-all" style={{ background: m.accent, opacity: isHover ? 1 : 0.3 }} />
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-md flex items-center justify-center transition-all" style={{ background: `${m.accent}1A`, color: m.accent, transform: isHover ? 'rotate(-6deg) scale(1.1)' : 'rotate(0)' }}>
                      {m.logo ? <img src={m.logo} alt="" className="h-9 w-9 object-contain" /> : <Icon className="w-6 h-6" strokeWidth={1.6} />}
                    </div>
                    <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-current transition-all" style={{ color: isHover ? m.accent : '#D1D5DB', transform: isHover ? 'translateX(4px)' : 'none' }} />
                  </div>
                  <div className="label-tag mb-2" style={{ color: m.accent }}>Module</div>
                  <h3 className="font-display font-semibold text-lg text-navy mb-2 leading-tight">
                    {m.short}
                  </h3>
                  <p className="text-sm text-neutral-600 leading-relaxed mb-3">{v.does}</p>
                  <div className={`overflow-hidden transition-all duration-300 ${isHover ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="pt-3 border-t border-neutral-100 mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">For</div>
                      <p className="text-xs text-neutral-700 leading-relaxed mb-3">{v.for}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: m.accent }}>
                        Enter Module <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PLANS / PRICING with currency conversion */}
      <section id="pricing" className="py-20 lg:py-24 bg-neutral-50">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="label-tag text-amber mb-4">Plans</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
              Pay for what you run. Scale as you grow.
            </h2>
            <p className="mt-4 text-neutral-600">
              Each module is priced independently. Tools unlock as your plan grows. Multi-currency
              billing — pay in Naira, Pounds or Dollars.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { name: 'Starter', priceNgn: 19500, sub: '/ month per module', features: ['Up to 25 users', 'Core tools per module', 'Email support', 'Standard SLA'], featured: false },
              { name: 'Growth', priceNgn: 65000, sub: '/ month per module', features: ['Up to 200 users', 'Expanded tool access', 'Priority support', 'Advanced analytics', '99.9% SLA'], featured: true },
              { name: 'Enterprise', priceNgn: null, sub: 'tailored quote', features: ['Unlimited users', 'Full tool access', 'Dedicated success manager', 'Data residency', 'White-glove onboarding'], featured: false },
            ].map((p) => {
              const price = p.priceNgn ? fmtPrice(p.priceNgn) : null;
              return (
                <div
                  key={p.name}
                  className={`relative p-7 rounded-md transition-all ${p.featured ? 'bg-navy text-white border border-amber shadow-xl scale-[1.02]' : 'bg-white border border-neutral-200 hover:border-navy/30'}`}
                  data-testid={`pricing-${p.name.toLowerCase()}`}
                >
                  {p.featured && (
                    <div className="absolute -top-3 left-7 px-3 py-1 bg-amber text-navy text-[10px] uppercase tracking-[0.18em] font-bold rounded-sm">Most chosen</div>
                  )}
                  <div className={`font-display font-semibold text-2xl mb-2 ${p.featured ? 'text-white' : 'text-navy'}`}>{p.name}</div>
                  {price ? (
                    <>
                      <div className="flex items-baseline gap-1.5 mb-1">
                        <span className={`font-display font-bold text-3xl ${p.featured ? 'text-amber' : 'text-navy'}`}>{price.ngn}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 mb-2 text-xs">
                        <span className={p.featured ? 'text-white/70' : 'text-neutral-500'}>🇬🇧 {price.gbp}</span>
                        <span className={p.featured ? 'text-white/40' : 'text-neutral-300'}>·</span>
                        <span className={p.featured ? 'text-white/70' : 'text-neutral-500'}>🇺🇸 {price.usd}</span>
                      </div>
                    </>
                  ) : (
                    <div className={`font-display font-bold text-3xl mb-3 ${p.featured ? 'text-amber' : 'text-navy'}`}>Custom</div>
                  )}
                  <div className={`text-xs ${p.featured ? 'text-white/60' : 'text-neutral-500'} mb-6`}>{p.sub}</div>
                  <ul className="space-y-2 mb-7">
                    {p.features.map((f) => (
                      <li key={f} className={`text-sm flex items-start gap-2 ${p.featured ? 'text-white/85' : 'text-neutral-700'}`}>
                        <span className={`h-1.5 w-1.5 rounded-full mt-2 flex-shrink-0 ${p.featured ? 'bg-amber' : 'bg-navy'}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#contact" className={`block text-center w-full py-2.5 rounded-md font-medium text-sm transition-all ${p.featured ? 'bg-amber text-navy hover:bg-gold' : 'bg-navy text-white hover:bg-navy-500'}`}>
                    Choose {p.name}
                  </a>
                </div>
              );
            })}
          </div>

          <div className="mt-10 max-w-4xl mx-auto bg-white border border-neutral-200 rounded-md p-5 flex flex-wrap items-center justify-around gap-4 text-sm">
            <span className="text-neutral-500 text-xs uppercase tracking-wider font-semibold">We collect in</span>
            {Object.entries(CURRENCY_RATES).map(([code, c]) => (
              <div key={code} className="flex items-center gap-2">
                <span className="text-2xl">{c.flag}</span>
                <div>
                  <div className="font-display font-semibold text-navy">{c.name}</div>
                  <div className="text-xs text-neutral-500 font-mono">{c.symbol} {code}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT — replaces "Get Started", with size, modules, tools */}
      <ContactSection />

      <Footer />
      <AIChatWidget context="Visitor on ATTP NEXUS public landing page." />
    </div>
  );
}

function ContactSection() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', organization: '', org_size: '', country: '',
    modules: [], tools_interest: '', message: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const toggleMod = (slug) => setForm((f) => ({ ...f, modules: f.modules.includes(slug) ? f.modules.filter(x => x !== slug) : [...f.modules, slug] }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await api.post('/contact/submit', form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-tag text-amber mb-4">Get in touch</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight text-balance">
              Let's run your organization on rails built for Africa.
            </h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Tell us your size, the modules you need, and the specific tools you want to use.
              We'll come back with a tailored proposal within 24 hours.
            </p>

            <div className="mt-8 space-y-3">
              <a href="mailto:info@attpnexus.com.ng" className="flex items-start gap-3 group">
                <div className="h-10 w-10 rounded-md bg-amber/15 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Email</div>
                  <div className="text-navy font-semibold group-hover:text-amber transition-colors">info@attpnexus.com.ng</div>
                </div>
              </a>
              <a href="tel:+2347033137679" className="flex items-start gap-3 group">
                <div className="h-10 w-10 rounded-md bg-amber/15 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Phone</div>
                  <div className="text-navy font-semibold group-hover:text-amber transition-colors">+234 (703) 313-7679</div>
                </div>
              </a>
            </div>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">Headquarters</div>
              <div className="grid grid-cols-2 gap-2">
                {HEADQUARTERS.map((hq) => (
                  <div key={hq.country} className="flex items-start gap-2 p-3 bg-neutral-50 rounded-md border border-neutral-200">
                    <span className="text-xl">{hq.flag}</span>
                    <div>
                      <div className="text-sm font-semibold text-navy">{hq.city}</div>
                      <div className="text-[11px] text-neutral-500">{hq.state}, {hq.country}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-neutral-50 border border-neutral-200 rounded-md p-6">
            <h3 className="font-display font-semibold text-xl text-navy mb-1">Tell us about your organization</h3>
            <p className="text-sm text-neutral-500 mb-5">All fields help us tailor your proposal.</p>

            {done ? (
              <div className="p-8 bg-emerald-50 border border-emerald-200 rounded-md text-center">
                <div className="text-emerald-700 font-display font-bold text-xl mb-2">Thank you!</div>
                <p className="text-sm text-emerald-700">Your enquiry has been received. We will be in touch within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Full name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                  <Input label="Email *" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <Input label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                  <Input label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} placeholder="Nigeria" />
                  <Input label="Organization name *" required value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} />
                  <Select label="Organization size" value={form.org_size} onChange={(v) => setForm({ ...form, org_size: v })}
                    options={['', '1-10 staff', '11-50 staff', '51-200 staff', '201-500 staff', '501-1000 staff', '1000+ staff']} />
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-2">Modules of interest</span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-44 overflow-y-auto p-2 bg-white border border-neutral-200 rounded">
                    {MODULES.map((m) => (
                      <label key={m.id} className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${form.modules.includes(m.slug) ? 'bg-amber/15 border border-amber/40' : 'hover:bg-neutral-50'}`}>
                        <input type="checkbox" checked={form.modules.includes(m.slug)} onChange={() => toggleMod(m.slug)} className="rounded" />
                        <span className="truncate">{m.short}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">Specific tools you want to use</span>
                  <textarea rows={2} value={form.tools_interest} onChange={(e) => setForm({ ...form, tools_interest: e.target.value })} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy bg-white" placeholder="e.g. Invoices, Asset Register, Fee Bills, OPD Queue…" />
                </div>

                <div>
                  <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">What are you trying to solve? *</span>
                  <textarea required rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy bg-white" />
                </div>

                {error && <div className="p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">{error}</div>}

                <button type="submit" disabled={busy} className="btn-accent w-full disabled:opacity-60" data-testid="contact-submit">
                  {busy ? 'Sending…' : 'Send enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Input({ label, value, onChange, type = 'text', required, placeholder }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">{label}</span>
      <input type={type} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy bg-white" />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy bg-white">
        {options.map((o) => <option key={o} value={o}>{o || 'Select…'}</option>)}
      </select>
    </label>
  );
}
